import { TriggerbeeApi, TriggerbeeRequester } from './../../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../../core/logger.service';
import { FilterProperty } from './../../search/filter-property';
import { WidgetService } from './widget.service';
import { WidgetSimpleInfo } from '../../campaign/widgets.model';
import { from, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { AutomationHelpers } from '../../automations/automation-helpers';
import { TranslateService } from '@ngx-translate/core';
import { IdentityService } from 'app/core/identity/identity.service';
import { CouponComponent, EventDefinition, EventScopeOption } from '../../automations/automations-models';
import { CouponsService } from 'app/coupons/coupons.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { filter, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class SelectionsService {
  private readonly logger: Logger;
  private readonly apiBaseUrl: string;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    private widgetService: WidgetService,
    private readonly translateService: TranslateService,
    private readonly identityService: IdentityService,
    private readonly couponsService: CouponsService,
    private readonly currentContextService: CurrentContextService,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('SelectionsService');
    this.apiBaseUrl = http.getBaseUrl(TriggerbeeApi.Gateway);
  }

  public async getGoalsAsync(): Promise<FilterProperty[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const response = await this.http.get(`${baseUrl}/goals`).toPromise();
    return response.map((x) => new FilterProperty().set(x.name, x.name));
  }

  public getGoals(): Observable<FilterProperty[]> {
    return from(this.getGoalsAsync());
  }

  public async getWidgetsAsFilterPropertiesAsync(): Promise<FilterProperty[]> {
    const widgetFilterProperties = [] as Array<FilterProperty>;

    const allWidgets = await this.widgetService.getAllWidgetsSimpleInfo().toPromise();

    if (allWidgets) {
      allWidgets.forEach((widget: WidgetSimpleInfo) => {
        widgetFilterProperties.push({
          id: widget.id.toString(),
          displayName: AutomationHelpers.getDynamicWidgetName(widget, this.translateService),
        } as FilterProperty);
      });
    }
    widgetFilterProperties.sort((a, b) => (a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1));

    return widgetFilterProperties;
  }

  public async getAllWidgetsSimpleInfosAsync(): Promise<Array<WidgetSimpleInfo>> {
    return await this.widgetService.getAllWidgetsSimpleInfo().toPromise();
  }

  // Every coupon in one tier. The catalogue used to split these into "has traffic" and the rest, but it
  // now records what is already set up to trigger on an event - which is the opposite of what someone
  // picking a new one wants on top, and empty until the first automation exists.
  public async getEventScopeOptionsAsync(keepScope?: string): Promise<Array<EventScopeOption>> {
    const [components, widgets, couponSets] = await Promise.all([
      this.getCouponComponentsAsync(),
      this.getAllWidgetsSimpleInfosAsync(),
      this.getCouponSetsAsync(),
    ]);

    const setNames = new Map((couponSets ?? []).map((set) => [set.id, set.name]));
    const all = (components ?? []).map((component) => {
      const campaign = AutomationHelpers.getEventCampaignLabel(component.widgetId, widgets, this.translateService);
      const coupon = AutomationHelpers.describeCoupon(component, setNames);
      return {
        value: AutomationHelpers.buildEventScopeValue(component.widgetId, component.componentId),
        label: coupon ? `${campaign} - ${coupon}` : campaign,
      };
    });

    // A saved scope whose component is gone still has to be listed, or editing silently drops it.
    if (keepScope && !all.some((scope) => scope.value === keepScope)) {
      const scope = AutomationHelpers.parseEventScopeValue(keepScope);
      all.push({
        value: keepScope,
        label: AutomationHelpers.getEventDefinitionLabel(
          { widgetId: scope.widgetId, componentId: scope.componentId } as EventDefinition,
          widgets,
          this.translateService,
        ),
      });
    }

    return all;
  }

  public async getEventDefinitionsAsync(): Promise<Array<EventDefinition>> {
    try {
      const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
      return await this.http.get(`${baseUrl}/event-definitions`).toPromise();
    } catch (error) {
      this.logger.error(`Could not load event definitions: ${error}`);
      return [];
    }
  }

  // Coupon name per scope, for anywhere a saved trigger is described rather than picked.
  public async getCouponNamesByScopeAsync(): Promise<Map<string, string>> {
    const [components, couponSets] = await Promise.all([this.getCouponComponentsAsync(), this.getCouponSetsAsync()]);
    const setNames = new Map((couponSets ?? []).map((set) => [set.id, set.name]));
    const names = new Map<string, string>();
    (components ?? []).forEach((component) => {
      const coupon = AutomationHelpers.describeCoupon(component, setNames);
      if (coupon) {
        names.set(AutomationHelpers.buildEventScopeValue(component.widgetId, component.componentId), coupon);
      }
    });
    return names;
  }

  public async getCouponComponentsAsync(): Promise<Array<CouponComponent>> {
    try {
      return await firstValueFrom(this.widgetService.getCouponComponents());
    } catch (error) {
      this.logger.error(`Could not load coupon components: ${error}`);
      return [];
    }
  }

  private async getCouponSetsAsync() {
    try {
      const context = await firstValueFrom(
        this.currentContextService.currentContextBS.pipe(
          filter((x) => !!x?.customer?.id),
          take(1),
        ),
      );
      return await firstValueFrom(this.couponsService.getCoupons(context.customer.id));
    } catch (error) {
      this.logger.error(`Could not load coupon sets: ${error}`);
      return [];
    }
  }

  public async getLabelsAsync(): Promise<FilterProperty[]> {
    const response = await this.requester.getAsync<string[]>(TriggerbeeApi.Gateway, 'tags');
    if (response.isPositive()) {
      return response.data.map((x) => new FilterProperty().set(x, this.getTagDisplayName(x)));
    } else {
      this.logger.error(`Could not get tags : ${response.toString()}`);
    }
  }

  public async getInterestProfilesAsync(): Promise<FilterProperty[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const response = await this.http.get(`${baseUrl}/interest-profiles`).toPromise();
    return response.map((x) => new FilterProperty().set(x.id, x.name));
  }

  private getTagDisplayName(tag: string): string {
    switch (tag) {
      case 'CurrentCustomer':
        return 'Customer';
      case 'Isp':
        return 'Internet service provider';
      default:
        return tag;
    }
  }

  public async getPersonsAsync(siteId): Promise<FilterProperty[]> {
    const excludeAdmins = this.identityService.currentIdentity.isAdmin() ? false : true;
    const response = await this.requester.getAsync<FilterProperty[]>(
      TriggerbeeApi.Gateway,
      `/users/accounts/${siteId}?excludeAdmins=${excludeAdmins}`,
    );
    if (!response.isPositive()) {
      this.logger.error(`Could not get Persons : ${response.toString()}`);
    }
    return response.data;
  }

  public async getZaps(): Promise<any> {
    const response = await this.requester.getAsync<FilterProperty[]>(
      TriggerbeeApi.Gateway,
      '/integrations/zapier/zaps',
    );
    if (!response.isPositive()) {
      this.logger.error(`Could not get zaps : ${response.toString()}`);
    }
    return response.data;
  }

  public async getVisitedPages(): Promise<FilterProperty[]> {
    const visitedPages = await this.http.get(`${this.apiBaseUrl}/goals/page-views`).toPromise();
    return visitedPages.map((x) => {
      return <FilterProperty>{
        id: x.name,
        displayName: x.name?.replace('visitedpage-', ''),
      };
    });
  }

  public async getVersions(): Promise<FilterProperty[]> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/consent-versions';
    const response = await this.http.get(url).toPromise();
    return response.map((m) => new FilterProperty().set(m.name, m.name));
  }
}
